/* eslint-disable prettier/prettier */
import { memo, useMemo, useState } from 'react';
import { Group } from 'react-konva';

import { ArrowShape } from '@/features/canvas/components/ArrowShape';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { Point } from '@/features/canvas/types/point';
import { calculateAbsolutePositionOnCanvasFromPercentageToPx } from '@/features/canvas/utils/calculate-absolute-position-on-canvas-in-px';
import { getNodeRadius } from '@/features/canvas/utils/get-node-radius';
import { components } from '@/types/api';

export type IsNodeActiveMap = Map<number, boolean>;
export type IsNodeDisabledMap = Map<number, boolean>;

type ItemProps = {
  dependencyX: number;
  dependencyY: number;
  nodeX: number;
  nodeY: number;
  fill?: string;
  opacity?: number;
  sourceSize?: number;
  targetSize?: number;
};

const positioningFactorCache = new Map<number, number>();

const VISUAL_ARROW_TRIM_PX = 10;

const getPositioningFactor = (size?: number): number => {
  if (size !== undefined && positioningFactorCache.has(size)) {
    return positioningFactorCache.get(size)!;
  }

  const radius = getNodeRadius(size);
  const factor = VISUAL_ARROW_TRIM_PX / radius;

  if (size !== undefined) {
    positioningFactorCache.set(size, factor);
  }

  return factor;
};

const ARROW_HEAD_LENGTH = -10;

const getAdjustedPoint = (
  from: { x: number; y: number },
  to: { x: number; y: number },
  size?: number
) => {
  const radius = getNodeRadius(size);
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const arrowHeadCompensation = ARROW_HEAD_LENGTH;

  return {
    x: from.x + Math.cos(angle) * (radius + arrowHeadCompensation),
    y: from.y + Math.sin(angle) * (radius + arrowHeadCompensation),
  };
};

const Item = memo<ItemProps>(
  ({ nodeX, nodeY, dependencyX, dependencyY, sourceSize, targetSize, opacity, fill }) => {
    const source = useMemo(() => ({ x: dependencyX, y: dependencyY }), [dependencyX, dependencyY]);
    const target = useMemo(() => ({ x: nodeX, y: nodeY }), [nodeX, nodeY]);

    const adjustedSource = getAdjustedPoint(source, target, sourceSize);
    const adjustedTarget = getAdjustedPoint(target, source, targetSize);

    return (
      <ArrowShape source={adjustedSource} target={adjustedTarget} opacity={opacity} fill={fill} />
    );
  }
);

const NodeDependencies = ({
  nodeDependencies,
  nodeId,
  nodePosition,
  canvasHeightToFit,
  canvasWidthToFit,
  offset,
  isNodeActive,
  isNodeDisabled,
}: {
  canvasHeightToFit: number;
  canvasWidthToFit: number;
  isNodeActive: IsNodeActiveMap;
  isNodeDisabled: IsNodeDisabledMap;
  nodeDependencies: components['schemas']['Dependencies'][];
  nodeId: number;
  nodePosition: Point;
  offset: Point;
}) => {
  const [hoveredDependency, setHoveredDependency] = useState<[number, number] | null>(null);

  const {
    filteredNodes,
    activeDependencyConnectionIds,
    activeDependencyNodeIds,
    setActiveDependencyConnectionIds,
    setActiveDependencyNodeIds,
    showDependencies,
    activeDisabledConnectionIds,
    setActiveDisabledConnectionIds,
    viewMode,
  } = useEditorContext();

  const makeKey = (m: number, s: number) => `${m}-${s}`;

  const recomputeActiveNodes = (
    activeConnections: Set<string>,
    disabledConnections: Set<string>
  ) => {
    const nodes = new Set<number>();
    
    const allGraphConnections = new Set<string>();
    filteredNodes.forEach(node => {
      node.dependencies?.forEach(dep => {
        allGraphConnections.add(`${dep.masterNodeId}-${dep.slaveNodeId}`);
      });
      
      filteredNodes.forEach(otherNode => {
        otherNode.dependencies?.forEach(dep => {
          if (dep.masterNodeId === node.id) {
            allGraphConnections.add(`${dep.masterNodeId}-${dep.slaveNodeId}`);
          }
        });
      });
    });
    
    activeConnections.forEach(key => {
      const [m, s] = key.split('-').map(Number);
      nodes.add(m);
      nodes.add(s);
    });
    
    disabledConnections.forEach(disabledKey => {
      const [disabledMaster, disabledSlave] = disabledKey.split('-').map(Number);
      
      const masterConnections = Array.from(allGraphConnections).filter(conn => {
        const [m, s] = conn.split('-').map(Number);
        return m === disabledMaster || s === disabledMaster;
      });
      
      const slaveConnections = Array.from(allGraphConnections).filter(conn => {
        const [m, s] = conn.split('-').map(Number);
        return m === disabledSlave || s === disabledSlave;
      });
      
      const masterHasActive = masterConnections.some(conn => 
        activeConnections.has(conn) && conn !== disabledKey
      );
      
      const slaveHasActive = slaveConnections.some(conn => 
        activeConnections.has(conn) && conn !== disabledKey
      );
      
      if (masterHasActive) nodes.add(disabledMaster);
      if (slaveHasActive) nodes.add(disabledSlave);
    });
    
    setActiveDependencyNodeIds(nodes);
  };

  const handleDependencyLineClick = ([master, slave]: [number, number]) => {
    if (!showDependencies) return;

    if (viewMode === 'budget') {
      // If no dependency chain is active (default state), allow interaction with all dependencies
      const isDefaultState = activeDependencyNodeIds.size === 0;
      
      // Allow interaction if it's the default state OR both nodes are in the active chain
      const canInteractWithDependency = isDefaultState || 
        (activeDependencyNodeIds.has(master) && activeDependencyNodeIds.has(slave));
      
      if (!canInteractWithDependency) {
        // Don't allow interaction with greyed out nodes
        return;
      }

      return;
    }

    // Original dependency chain logic for filter mode
    const key = `${master}-${slave}`;
    const nextConnections = new Set(activeDependencyConnectionIds);
    const nextDisabled = new Set(activeDisabledConnectionIds);
  
    const bothNodesActive =
      activeDependencyNodeIds.has(master) && activeDependencyNodeIds.has(slave);
  
    if (nextConnections.has(key) && !nextDisabled.has(key)) {
      nextConnections.delete(key);
      nextDisabled.add(key);
    } else if (nextDisabled.has(key)) {
      nextDisabled.delete(key);
      nextConnections.add(key);
    } else if (bothNodesActive) {
      nextDisabled.add(key);
    } else {
      nextConnections.add(key);
    }
  
    setActiveDependencyConnectionIds(nextConnections);
    setActiveDisabledConnectionIds(nextDisabled);
    recomputeActiveNodes(nextConnections, nextDisabled);
  };

  const dependenciesToRender = useMemo(
    () =>
      nodeDependencies.filter(
        dep =>
          filteredNodes.some(n => n.id === dep.masterNodeId && n.isVisible) &&
          filteredNodes.some(n => n.id === dep.slaveNodeId && n.isVisible)
      ),
    [nodeDependencies, filteredNodes]
  );

  const getDependencyOpacity = (m: number, s: number): number => {
    const key = makeKey(m, s);

    // In budget mode, handle visibility differently
    if (viewMode === 'budget') {
      // If dependencies are on but no chain has been selected (default state)
      const isDefaultState = activeDependencyNodeIds.size === 0;
      
      if (isDefaultState) {
        // Show all arrows in default state
        if (hoveredDependency?.[0] === m && hoveredDependency?.[1] === s) return 1;
        return 1; // Show all arrows by default
      }
      
      // If a chain has been selected, only show arrows for nodes in the chain
      const masterInChain = activeDependencyNodeIds.has(m);
      const slaveInChain = activeDependencyNodeIds.has(s);
      
      // Hide arrow only if one of the nodes is NOT in the active chain
      if (!masterInChain || !slaveInChain) return 0;
      
      // Both nodes are in the chain - show the arrow regardless of toggle state
      // The arrow represents the dependency relationship, not the node visibility
      if (hoveredDependency?.[0] === m && hoveredDependency?.[1] === s) return 1;
      
      return 1;
    }

    // Original logic for filter mode
    if (hoveredDependency?.[0] === m && hoveredDependency?.[1] === s) return 1;

    if (activeDisabledConnectionIds.has(key)) return 0.15;

    if (activeDependencyNodeIds.size === 0) return 1;

    if (activeDependencyConnectionIds.has(key)) return 1;

    const bothNodesActive = activeDependencyNodeIds.has(m) && activeDependencyNodeIds.has(s);

    return bothNodesActive ? 1 : 0.15;
  };

  return (
    <Group key={nodeId}>
      {dependenciesToRender.map(dependency => {
        const dependencyNode = filteredNodes.find(fNode => fNode.id === dependency.masterNodeId)!;
        const dependencySize = dependencyNode.size;
        const targetSize = filteredNodes.find(n => n.id === nodeId)?.size;

        const dependencyRadius = getNodeRadius(dependencySize);
        const correctionFactor = getPositioningFactor(dependencySize);

        const { x: dependencyX, y: dependencyY } =
          calculateAbsolutePositionOnCanvasFromPercentageToPx({
            height: canvasHeightToFit,
            width: canvasWidthToFit,
            xPercentage: dependencyNode.x,
            yPercentage: dependencyNode.y,
            offsetX: offset.x,
            offsetY: offset.y,
            additionX: dependencyRadius * correctionFactor,
            additionY: dependencyRadius * correctionFactor,
          });

        const masterActive = isNodeActive.get(dependency.masterNodeId) ?? false;
        const slaveActive = isNodeActive.get(dependency.slaveNodeId) ?? false;

        const masterDisabled = isNodeDisabled.get(dependency.masterNodeId) ?? false;
        const slaveDisabled = isNodeDisabled.get(dependency.slaveNodeId) ?? false;

        const visuallyGrey = !(masterActive && slaveActive);

        const behaviourDisabled = masterDisabled || slaveDisabled;

        const isHovered =
          hoveredDependency?.[0] === dependency.masterNodeId &&
          hoveredDependency?.[1] === dependency.slaveNodeId;

        let arrowOpacity: number;

        // In budget mode, use our custom opacity logic
        if (viewMode === 'budget') {
          arrowOpacity = getDependencyOpacity(dependency.masterNodeId, dependency.slaveNodeId);
        } else if (behaviourDisabled) {
          arrowOpacity = 0.15;
        } else if (isHovered) {
          arrowOpacity = 1;
        } else if (visuallyGrey) {
          arrowOpacity = 0.15;
        } else {
          arrowOpacity = getDependencyOpacity(dependency.masterNodeId, dependency.slaveNodeId);
        }

        let arrowColor = '#6e6e72';
        if (!behaviourDisabled && isHovered && viewMode !== 'budget') arrowColor = '#2cbef4';
        if (viewMode === 'budget' && isHovered && arrowOpacity > 0) arrowColor = '#2cbef4';

        // In budget mode, completely hide arrows that have 0 opacity
        if (viewMode === 'budget' && arrowOpacity === 0) {
          return null;
        }

        // In budget mode, check if we should allow interaction
        const canInteract = viewMode === 'budget' 
          ? (activeDependencyNodeIds.size === 0 || // Default state - all dependencies are interactive
             (activeDependencyNodeIds.has(dependency.masterNodeId) && activeDependencyNodeIds.has(dependency.slaveNodeId)))
          : !behaviourDisabled;

        return (
          <Group
            key={`${dependency.masterNodeId}-${dependency.slaveNodeId}`}
            onClick={
              canInteract
                ? () => handleDependencyLineClick([dependency.masterNodeId, dependency.slaveNodeId])
                : undefined
            }
            listening={canInteract}
            onMouseEnter={() => {
              if (canInteract) {
                setHoveredDependency([dependency.masterNodeId, dependency.slaveNodeId]);
              }
            }}
            onMouseLeave={() => setHoveredDependency(null)}
          >
            <Item
              dependencyX={dependencyX}
              dependencyY={dependencyY}
              nodeX={nodePosition.x}
              nodeY={nodePosition.y}
              sourceSize={dependencySize}
              targetSize={targetSize}
              opacity={arrowOpacity}
              fill={arrowColor}
            />
          </Group>
        );
      })}
    </Group>
  );
};

export { NodeDependencies };
